import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Star, MapPin, ShieldCheck, Zap, CheckCircle2, AlertTriangle, Sparkles,
  Users, Loader2,
} from "lucide-react";

export default function Marketplace() {
  const [catalog, setCatalog] = useState({ sports: [], config: {} });
  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  return (
    <div className="fade-up">
      <PageHeader overline="Transactional workforce marketplace" title="Krida Marketplace"
        subtitle="Discover and book verified coaches and officials. Explainable Smart Assign, powered by the CRPN network — never a black box." testid="marketplace-page" />

      <Tabs defaultValue="referee">
        <TabsList className="bg-krida-surface border border-white/10 mb-6">
          <TabsTrigger value="referee" data-testid="tab-referee" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><ShieldCheck className="w-4 h-4" /> Referees & Officials</TabsTrigger>
          <TabsTrigger value="coach" data-testid="tab-coach" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><Users className="w-4 h-4" /> Coaches</TabsTrigger>
          <TabsTrigger value="smart" data-testid="tab-smart" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><Sparkles className="w-4 h-4" /> Smart Assign</TabsTrigger>
        </TabsList>

        <TabsContent value="referee"><RefereeMarket catalog={catalog} /></TabsContent>
        <TabsContent value="coach"><CoachMarket catalog={catalog} /></TabsContent>
        <TabsContent value="smart"><SmartAssign catalog={catalog} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Filters({ catalog, filters, setFilters }) {
  return (
    <div className="flex flex-wrap gap-3 mb-6">
      <Sel value={filters.sport} onChange={(v) => setFilters((f) => ({ ...f, sport: v }))} label="Sport"
        options={["all", ...catalog.sports.map((s) => s.name)]} testid="mkt-filter-sport" />
      <Sel value={filters.city} onChange={(v) => setFilters((f) => ({ ...f, city: v }))} label="City"
        options={["all", "Chennai", "Bengaluru", "Mumbai", "Hyderabad", "Delhi", "Pune", "Kochi"]} testid="mkt-filter-city" />
    </div>
  );
}

function RefereeMarket({ catalog }) {
  const [filters, setFilters] = useState({ sport: "Basketball", city: "all" });
  const [refs, setRefs] = useState(null);
  useEffect(() => {
    const params = {};
    if (filters.sport !== "all") params.sport = filters.sport;
    if (filters.city !== "all") params.city = filters.city;
    api.get("/marketplace/referees", { params }).then((r) => setRefs(r.data.referees)).catch(() => setRefs(false));
  }, [filters]);

  return (
    <div>
      <Filters catalog={catalog} filters={filters} setFilters={setFilters} />
      {refs === null ? <Loader /> : refs.length === 0 ? <Empty label="No officials match" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {refs.map((p) => <ProCard key={p.id} p={p} fee={`₹${p.match_fee?.toLocaleString("en-IN")} / match`} />)}
        </div>
      )}
    </div>
  );
}

function CoachMarket({ catalog }) {
  const [filters, setFilters] = useState({ sport: "Basketball", city: "all" });
  const [coaches, setCoaches] = useState(null);
  const [booking, setBooking] = useState(null);
  useEffect(() => {
    const params = {};
    if (filters.sport !== "all") params.sport = filters.sport;
    if (filters.city !== "all") params.city = filters.city;
    api.get("/marketplace/coaches", { params }).then((r) => setCoaches(r.data.coaches)).catch(() => setCoaches(false));
  }, [filters]);

  return (
    <div>
      <Filters catalog={catalog} filters={filters} setFilters={setFilters} />
      {coaches === null ? <Loader /> : coaches.length === 0 ? <Empty label="No coaches match" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {coaches.map((p) => <ProCard key={p.id} p={p} fee={`₹${p.hourly_rate?.toLocaleString("en-IN")} / hr`}
            cta="Book coach" onCta={() => setBooking(p)} />)}
        </div>
      )}
      {booking && <BookDialog coach={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}

function ProCard({ p, fee, cta, onCta }) {
  return (
    <div className="krida-card p-5" data-testid={`pro-card-${p.id}`}>
      <div className="flex items-center gap-3">
        <img src={p.photo} alt={p.name} className="w-14 h-14 rounded-xl object-cover border border-white/10" />
        <div className="min-w-0">
          <div className="font-semibold truncate flex items-center gap-1.5">{p.name}
            {p.verification_status === "Verified" && <ShieldCheck className="w-3.5 h-3.5 text-krida-emerald flex-shrink-0" />}</div>
          <div className="text-xs text-slate-500">{p.primary_role} · {p.primary_level}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
        <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-krida-amber" /> {p.rating}</span>
        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.location.city}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">{p.specialisation && <Tag tone="cobalt">{p.specialisation}</Tag>}<Tag>{p.experience_years}y exp</Tag></div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
        <span className="font-mono text-sm text-krida-emerald">{fee}</span>
        {cta && <button onClick={onCta} data-testid={`book-btn-${p.id}`} className="px-3 py-1.5 rounded-lg bg-krida-crimson hover:bg-krida-crimsonDark text-xs font-semibold transition-colors">{cta}</button>}
      </div>
    </div>
  );
}

function BookDialog({ coach, onClose }) {
  const svc = coach.services?.[0] || { title: "Individual Coaching", price: coach.hourly_rate };
  const [form, setForm] = useState({ service: svc.title, date: "", time: "16:00", venue: "", participants: 1, price: svc.price });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    try { await api.post("/marketplace/book", { coach_id: coach.id, ...form }); toast.success("Booking request sent to coach"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not book"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-krida-card border-white/10 text-slate-100">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Book {coach.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="overline text-slate-500 block mb-1">Service</label>
            <Select value={form.service} onValueChange={(v) => { const s = coach.services?.find((x) => x.title === v); set("service", v); if (s) set("price", s.price); }}>
              <SelectTrigger data-testid="book-service" className="bg-black/40 border-white/10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-krida-card border-white/10 text-slate-100">{(coach.services || [svc]).map((s) => <SelectItem key={s.title} value={s.title}>{s.title} — ₹{s.price}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline text-slate-500 block mb-1">Date</label><input data-testid="book-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" /></div>
            <div><label className="overline text-slate-500 block mb-1">Participants</label><input type="number" value={form.participants} onChange={(e) => set("participants", Number(e.target.value))} className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" /></div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5"><span className="text-sm text-slate-400">Estimated price</span><span className="font-mono text-lg text-krida-emerald">₹{form.price?.toLocaleString("en-IN")}</span></div>
          <button onClick={submit} data-testid="book-submit" className="w-full py-3 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold transition-colors">Send booking request</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SmartAssign({ catalog }) {
  const [req, setReq] = useState({ sport: "Basketball", role: "Referee", level: "State", city: "Chennai", count: 16, date: "", clubs: "Metro Sports Club" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setReq((r) => ({ ...r, [k]: v }));

  const runOverride = async (r) => {
    const reason = window.prompt(`Override conflict for ${r.name}? Enter a mandatory reason (logged to audit):`);
    if (!reason) return;
    try { await api.post("/assignments/override-conflict", { professional_id: r.professional_id, professional_name: r.name, context: `${req.sport} ${req.role} — ${req.city}`, reason }); toast.success("Conflict override recorded to audit log"); }
    catch (e) { toast.error(e.response?.data?.detail || "Override failed"); }
  };

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/marketplace/smart-assign", {
        ...req, count: Number(req.count), use_ai: true,
        participating_clubs: (req.clubs || "").split(",").map((c) => c.trim()).filter(Boolean),
      });
      setResult(data);
    } catch (e) { toast.error("Smart Assign failed"); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="krida-card p-6 h-fit">
        <div className="flex items-center gap-2 mb-4"><Sparkles className="w-5 h-5 text-krida-crimson" /><h3 className="font-heading text-xl font-bold">Deployment request</h3></div>
        <div className="space-y-3">
          <Sel value={req.sport} onChange={(v) => set("sport", v)} label="Sport" options={catalog.sports.map((s) => s.name)} testid="sa-sport" />
          <Sel value={req.role} onChange={(v) => set("role", v)} label="Role" options={["Referee", "Umpire", "Table Official", "Match Commissioner", "Physiotherapist"]} testid="sa-role" />
          <Sel value={req.level} onChange={(v) => set("level", v)} label="Level" options={["District", "State", "National", "Elite"]} testid="sa-level" />
          <Sel value={req.city} onChange={(v) => set("city", v)} label="City" options={["Chennai", "Bengaluru", "Mumbai", "Hyderabad", "Delhi"]} testid="sa-city" />
          <div><label className="overline text-slate-500 block mb-1">Officials required</label><input data-testid="sa-count" type="number" value={req.count} onChange={(e) => set("count", e.target.value)} className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" /></div>
          <div><label className="overline text-slate-500 block mb-1">Required date</label><input data-testid="sa-date" type="date" value={req.date} onChange={(e) => set("date", e.target.value)} className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" /></div>
          <div><label className="overline text-slate-500 block mb-1">Participating clubs (conflict check)</label><input data-testid="sa-clubs" value={req.clubs} onChange={(e) => set("clubs", e.target.value)} placeholder="comma separated" className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" /></div>
          <button onClick={run} disabled={busy} data-testid="sa-run" className="w-full py-3 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4" /> Run Smart Assign</>}
          </button>
        </div>
      </div>

      <div className="lg:col-span-2">
        {busy && <Loader label="Ranking eligible professionals" />}
        {result && (
          <div data-testid="sa-results">
            {result.ai_summary && (
              <div className="krida-card p-4 mb-4 border-krida-emerald/20">
                <div className="overline text-krida-emerald mb-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Krida AI summary</div>
                <p className="text-sm text-slate-300">{result.ai_summary}</p>
              </div>
            )}
            <div className="flex items-center gap-2 mb-3 text-sm text-slate-400">
              <span className="font-mono text-krida-emerald text-lg font-bold">{result.eligible_count}</span> eligible professionals recommended
            </div>
            <div className="space-y-3">
              {result.recommendations.map((r) => (
                <div key={r.professional_id} className="krida-card p-4" data-testid={`sa-rec-${r.professional_id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl grid place-items-center font-mono font-bold text-lg flex-shrink-0 ${r.eligible ? "bg-krida-emerald/10 text-krida-emerald border border-krida-emerald/20" : "bg-slate-800 text-slate-400 border border-slate-700"}`}>{r.score}%</div>
                    <img src={r.photo} alt={r.name} className="w-11 h-11 rounded-lg object-cover border border-white/10" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold flex items-center gap-2">{r.name} <span className="text-xs text-slate-500">{r.level}</span></div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                        {r.reasons.map((reason, i) => <span key={i} className="text-xs text-slate-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-krida-emerald" /> {reason}</span>)}
                      </div>
                      {r.warnings?.length > 0 && (
                        <div className="flex flex-wrap gap-x-3 mt-1">{r.warnings.map((w, i) => <span key={i} className="text-xs text-krida-amber flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {w}</span>)}</div>
                      )}
                    </div>
                    {r.eligible ? <Tag tone="emerald">Eligible</Tag> : r.conflict ? (
                      <button onClick={() => runOverride(r)} data-testid={`sa-override-${r.professional_id}`} className="px-3 py-1.5 rounded-lg bg-rose-500/15 text-rose-400 text-xs font-semibold hover:bg-rose-500/25 transition-colors">Override conflict</button>
                    ) : <Tag tone="amber">Review</Tag>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {!result && !busy && <Empty label="Configure a request and run Smart Assign" />}
      </div>
    </div>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline text-slate-500 block mb-1">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-black/40 border-white/10 rounded-xl text-sm w-full"><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent className="bg-krida-card border-white/10 text-slate-100 max-h-72">{options.map((o) => <SelectItem key={o} value={o}>{o === "all" ? `All ${label}s` : o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
